import * as React from 'react';
import { History } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import { ProgrammeRow, ChildProgrammeStats } from './ParentClubProgrammes.types';
import { mergeProgrammeInboxItems, buildProgrammeRows, sortProgrammeRows } from './ParentClubProgrammes.helpers';
import { ParentClubProgrammesCard } from './ParentClubProgrammesCard';
import { loadParentClubProgrammesData } from './ParentClubProgrammes.loader';
import { ParentPageHeader } from '../components/ParentPageHeader';
import '../components/ParentLists.scss';
import './ParentClubProgrammes.scss';
import {
  ClubSectionCounts,
  clubSectionTabs,
  getParentClubSectionCounts,
  hasClubSectionContent
} from 'Src/helpers/club/clubSectionCounts';

interface Props {
  user: AppUser;
  history: History;
}

export const ParentClubProgrammes: React.FC<Props> = props => {
  const { user, history } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [rows, setRows] = React.useState<ProgrammeRow[]>([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [statsByProgrammeChild, setStatsByProgrammeChild] = React.useState<
    Record<string, Record<string, ChildProgrammeStats>>
  >({});
  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());
  const [sectionCounts, setSectionCounts] = React.useState<ClubSectionCounts | undefined>();

  React.useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const load = React.useCallback(async () => {
    try {
      setError(undefined);
      setIsLoading(true);

      const programmeData = await loadParentClubProgrammesData(user);
      const { childNameByPermId, flatItems } = programmeData;

      getParentClubSectionCounts(user, { programmes: programmeData })
        .then(setSectionCounts)
        .catch(() => setSectionCounts(undefined));

      if (!flatItems.length) {
        setRows([]);
        return;
      }

      const mergedItems = mergeProgrammeInboxItems(flatItems);
      const out: ProgrammeRow[] = sortProgrammeRows(buildProgrammeRows(mergedItems, childNameByPermId));

      const nextStats = out.reduce((result, row) => {
        result[String(row.id)] = (row.children && row.children.statsByPermissionId) || {};
        return result;
      }, {} as Record<string, Record<string, ChildProgrammeStats>>);
      setStatsByProgrammeChild(nextStats);

      setRows(out);
    } catch (e) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as any).message || '') : '';
      setError(msg || 'Failed to load programmes');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (isLoading) return <Loader />;

  return (
    <main className="parentPage parentListPage parentClubsPage">
      <ParentPageHeader
        title="Clubs"
        tabs={clubSectionTabs(sectionCounts, 'browse', history)}
        chips={[
          {
            value: rows.length,
            label: rows.length === 1 ? 'programme available' : 'programmes available',
            tone: rows.length ? 'green' : 'slate'
          }
        ]}
        actions={
          <button className="parentButton" type="button" onClick={load}>
            Refresh
          </button>
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!rows.length ? (
        <div className="parentListEmpty">
          <strong>{hasClubSectionContent(sectionCounts) ? 'No clubs available' : 'Nothing here yet'}</strong>
          <span>
            {hasClubSectionContent(sectionCounts)
              ? 'New programmes will appear here when your school opens them for booking.'
              : 'When your school sends or opens something, you will see it here.'}
          </span>
        </div>
      ) : (
        <div className="parentClubProgrammeGrid">
          {rows.map(row => (
            <ParentClubProgrammesCard
              key={String(row.id)}
              history={history}
              row={row}
              nowMs={nowMs}
              statsByProgrammeChild={statsByProgrammeChild}
            />
          ))}
        </div>
      )}
    </main>
  );
};
